import {autobind} from 'core-decorators';

import {Component} from 'inferno'; // eslint-disable-line @typescript-eslint/no-unused-vars

import {subscribe} from '../../../replete';

import {Button, CustomComponent, Link} from '../../common/components';
import {Icon} from '../../common/components/Icon';
import {ToggleMenu} from '../../common/components/ToggleMenu';
import dialog from '../../common/dialog';
import emitter from '../../common/util/emitter';

import {NotificationIcon} from './NotificationIcon';

import './NavBar.scss';
import '~/assets/scss/transformicons.scss';


export function NavMenu(props: jsx.Props, context: jsx.AppGlobals) {
    const gettextSub = context.gtService.gettextSub;
    const localizeUrlPath = context.gtService.localizeUrlPath;

    return (
        <div className="animated NavMenu">
            <div className="section">
                <div className="header">{gettextSub('Products')}</div>
                <ul>
                    <li><Link to="https://apps.apple.com/app/apple-store/id917932200?pt=39040802&ct=NavGifKeyboard&mt=8" external={true}>{gettextSub('GIF Keyboard')}</Link></li>
                    <li><Link to="https://play.google.com/store/apps/details?id=com.riffsy.FBMGIFApp" external={true}>{gettextSub('Android')}</Link></li>
                    <li><Link to={localizeUrlPath('/contentpartners')}>{gettextSub('Content Partners')}</Link></li>
                </ul>
            </div>
            <div className="section">
                <div className="header">{gettextSub('Explore')}</div>
                <ul>
                    <li><Button onClick={() => {
                        dialog.open('language-selection-dialog');
                    }}>{gettextSub('Language')}</Button></li>
                    <li><Link to={localizeUrlPath('/reactions')}>{gettextSub('Reaction GIFs')}</Link></li>
                    <li><Link to={localizeUrlPath('/explore')}>{gettextSub('Explore GIFs')}</Link></li>
                </ul>
            </div>
            <div className="section">
                <div className="header">{gettextSub('Company')}</div>
                <ul>
                    <li>
                        <Link to={localizeUrlPath('/about-us')}>{gettextSub('About')}</Link>
                    </li>
                    <li>
                        <Link to={localizeUrlPath('/press')}>{gettextSub('Press')}</Link>
                    </li>
                    <li>
                        <Link to="https://blog.tenor.com/" external={true}>{gettextSub('Blog')}</Link>
                    </li>
                    <li>
                        <Link to='https://support.google.com/tenor' external={true}>{gettextSub('FAQ')}</Link>
                    </li>
                    <li>
                        <Link to={localizeUrlPath('/legal-terms')}>{gettextSub('Terms and Privacy')}</Link>
                    </li>
                    <li>
                        <Link to="/assets/dist/licenses.txt" external={true}>{gettextSub('Website Licenses')}</Link>
                    </li>
                    <li>
                        <Link to={context.GOOGLE_CONTACT_URL}>{gettextSub('Contact Us')}</Link>
                    </li>
                </ul>
            </div>
            <div className="section">
                <div className="header">{gettextSub('API')}</div>
                <ul>
                    <li><Link to="/gifapi" external={true}>{gettextSub('Tenor GIF API')}</Link></li>
                    <li><Link to="https://developers.google.com/tenor/guides/endpoints" external={true}>{gettextSub('GIF API Documentation')}</Link></li>
                    <li><Link to="/gifapi/unity-ar-gif-sdk" external={true}>{
                        // Translators: Technical product name, can
                        // Translators: probably remain in English.
                        gettextSub('Unity AR SDK')
                    }</Link></li>
                </ul>
            </div>
        </div>
    );
}


@subscribe({
    auth: ['api.auth'],
})
export class NavBar extends CustomComponent {
    showEditProfileDialog() {
        dialog.open('edit-profile-dialog');
    }
    @autobind
    logout() {
        this.context.authService.logout({});
    }
    @autobind
    showLoginDialog() {
        this.context.apiService.registerEvent('signin_button_tap', {});
        this.context.authService.showLoginDialog();
    }
    render() {
        const gettextSub = this.context.gtService.gettextSub;
        const delocalizeUrlPath = this.context.gtService.delocalizeUrlPath;
        const localizeUrlPath = this.context.gtService.localizeUrlPath;
        const isGifMakerPage = delocalizeUrlPath(
            this.context.router.route.location.pathname) === '/gif-maker';

        const loggedIn = this.context.authService.isLoggedIn();
        let loggedInMenu = null;
        if (loggedIn) {
            loggedInMenu = (<ToggleMenu className="navbar-icon profile" menu={
                <div className="animated">
                    <Link className="item" to={this.linkToProfile()}>
                        <div className="username">
                            { this.context.authService.getUsername() }
                        </div>
                        <div className="subheading">
                            {gettextSub('view profile')}
                        </div>
                    </Link>
                    { this.context.authService.userHasFlag('developer') &&
                        <Link
                            className="item"
                            to="/developer/dashboard"
                            external={true}
                        >
                            {gettextSub('Developer Dashboard')}
                        </Link>
                    }
                    <Link
                        className="item edit-page"
                        onClick={this.showEditProfileDialog}
                    >
                        {gettextSub('Edit Profile')}
                    </Link>
                    <Link className="item" onClick={this.logout}>
                        {gettextSub('Log out')}
                    </Link>
                </div>}>
                <Icon name="profile-icon"/>
            </ToggleMenu>);
        }

        return (
            <nav className={`NavBar${isGifMakerPage ? ' blue-box-shadow' : ''}`}>
                <div className="container">
                    <span itemScope={true} itemType="http://schema.org/Organization">
                        <Link to={localizeUrlPath('/')} className="navbar-brand" itemProp="url">
                            <img
                                src="/assets/img/tenor-logo.svg"
                                width="80"
                                height="22"
                                alt={gettextSub('Tenor logo')}
                                itemProp="logo"
                            />
                        </Link>
                    </span>
                    <div className="nav-buttons">
                        <div className="account-buttons">
                            { !isGifMakerPage &&
                                <Link
                                    to={localizeUrlPath('/gif-maker?utm_source=nav-bar&utm_medium=internal&utm_campaign=gif-maker-entrypoints')}
                                    className="button upload-button"
                                    clickLabel={`navbar-upload-button`}
                                >
                                    <img
                                        src="/assets/icons/upload-icon.svg"
                                        width="14"
                                        height="17"
                                        alt={gettextSub('Upload icon')}
                                    />
                                    {gettextSub('Create')}
                                </Link>
                            }
                            { loggedIn && <NotificationIcon/> }
                            { loggedIn && loggedInMenu }

                            { !loggedIn && <button className="white-button" onClick={this.showLoginDialog}>
                                {gettextSub('SIGN IN')}
                            </button> }
                        </div>
                        <ToggleMenu
                            buttonClassName="menu-button navicon-button x"
                            menu={<NavMenu />}
                            closeOnScroll={true}
                        >
                            <div class="navicon"/>
                        </ToggleMenu>
                    </div>
                </div>
            </nav>
        );
    }
}
